<script setup lang="ts">
import { Link as InertiaLink } from '@inertiajs/vue3';
import { useSidebarStore } from '@/stores/sidebarStore.js';
import { computed } from 'vue';
import { IconApps, IconMenu2, IconSearch } from '@tabler/icons-vue';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { useUser } from '@/composables/useUser';
import { useUserMenuItems } from '@/composables/useUserMenuItems';
import { useGlobalSearch } from '@/composables/useGlobalSearch';
import { useAppNavigation } from '@/composables/useAppNavigation';

const store = useSidebarStore();

const sidebarOpen = computed<boolean>({
    get() {
        return store.open;
    },
    set(newValue: boolean) {
        store.toggleSidebar(newValue);
    },
});

const user = useUser();
const { openSearch } = useGlobalSearch();
const { items: userMenuItems } = useUserMenuItems();
const { createItems } = useAppNavigation();
</script>

<template>
    <header
        class="fixed left-0 top-0 z-header flex h-14 w-full items-center justify-between bg-primary-500 pr-[var(--scrollbar-width,0px)] md:h-16"
    >
        <div class="px-4 md:px-8">
            <InertiaLink
                :href="route('dashboard')"
                class="brand-main font-base hidden text-lg font-black not-italic tracking-wider text-white md:block"
            >
                <img
                    class="h-14 w-auto"
                    src="/images/logo.png"
                    alt="Maat Stalling"
                />
            </InertiaLink>

            <button
                type="button"
                class="flex min-h-11 min-w-11 cursor-pointer items-center justify-center overflow-visible rounded-sm border-0 bg-white ease-linear hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-500 md:ml-0 md:hidden"
                @click="sidebarOpen = true"
            >
                <span class="sr-only">Open sidebar</span>
                <IconMenu2
                    class="size-6 text-gray-500"
                    aria-hidden="true"
                />
            </button>
        </div>

        <div class="flex items-stretch h-14 md:h-16">
            <button
                type="button"
                data-testid="site-header-search"
                class="flex min-h-11 items-center justify-center gap-2 px-4 text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset"
                @click="openSearch"
            >
                <span class="sr-only">Zoeken</span>
                <IconSearch
                    class="size-5 shrink-0"
                    aria-hidden="true"
                />

                <kbd
                    class="shrink-0 hidden rounded bg-white/15 px-1.5 py-0.5 text-xs font-medium text-white/80 lg:inline-block"
                    aria-hidden="true"
                >
                    Ctrl/⌘ K
                </kbd>
            </button>

            <div
                class="hidden h-full md:block md:w-px md:bg-white/10"
                aria-hidden="true"
            />

            <DropdownMenu :items="createItems">
                <template #trigger>
                    <button
                        type="button"
                        data-testid="site-header-create"
                        class="inline-flex h-full items-center justify-center px-4 py-2.5 text-white hover:bg-white/10 hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset md:px-6"
                    >
                        <span class="sr-only">Add item</span>
                        <IconApps
                            class="size-6"
                            aria-hidden="true"
                        />
                    </button>
                </template>
            </DropdownMenu>

            <div
                class="hidden h-full lg:block lg:w-px lg:bg-white/10"
                aria-hidden="true"
            />

            <div class="flex w-full items-stretch">
                <DropdownMenu
                    :items="userMenuItems"
                    align="end"
                >
                    <template #trigger>
                        <button
                            type="button"
                            data-testid="site-header-user-menu"
                            class="inline-flex items-center gap-3 px-4 py-3 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset md:pl-6 md:pr-8"
                        >
                            <span class="sr-only">Open menu</span>
                            <img
                                class="size-8 rounded-full bg-gray-50"
                                src="/images/avatar.png"
                                :alt="user.name"
                            />
                            <span class="hidden flex-col lg:flex lg:items-start text-sm">
                                <span
                                    class="font-semibold text-white"
                                    aria-hidden="true"
                                >
                                    {{ user.name }}
                                </span>
                                <span
                                    class="text-xs text-white"
                                    aria-hidden="true"
                                >
                                    {{ user.email }}
                                </span>
                            </span>
                        </button>
                    </template>
                </DropdownMenu>
            </div>
        </div>
    </header>
</template>
